import { isCtrlOrMetaPressed } from 'roosterjs-editor-dom';
import { Keys, PluginEventType } from 'roosterjs-editor-types';
import type {
    EditPluginState,
    GenericContentEditFeature,
    IEditor,
    PluginEvent,
    PluginWithState,
} from 'roosterjs-editor-types';

/**
 * Edit Component helps handle Content edit features
 */
class EditPlugin implements PluginWithState<EditPluginState> {
    private editor: IEditor | null = null;
    private state: EditPluginState;

    /**
     * Construct a new instance of EditPlugin
     * @param options The editor options
     */
    constructor() {
        this.state = {
            features: {},
        };
    }

    /**
     * Get a friendly name of  this plugin
     */
    getName() {
        return 'Edit';
    }

    /**
     * Initialize this plugin. This should only be called from Editor
     * @param editor Editor instance
     */
    initialize(editor: IEditor) {
        this.editor = editor;
    }

    /**
     * Dispose this plugin
     */
    dispose() {
        this.editor = null;
    }

    /**
     * Get plugin state object
     */
    getState() {
        return this.state;
    }

    /**
     * Handle events triggered from editor
     * @param event PluginEvent object
     */
    onPluginEvent(event: PluginEvent) {
        let hasFunctionKey = false;
        let features: GenericContentEditFeature<PluginEvent>[] | null = null;
        let ctrlOrMeta = false;
        const isKeyDownEvent = event.eventType == PluginEventType.KeyDown;

        if (isKeyDownEvent) {
            const rawEvent = event.rawEvent;
            const range = this.editor?.getSelectionRange();

            ctrlOrMeta = isCtrlOrMetaPressed(rawEvent);
            hasFunctionKey = ctrlOrMeta || rawEvent.altKey;
            features =
                this.state.features[rawEvent.which] ||
                (range && !range.collapsed && this.state.features[Keys.RANGE]);
        } else if (event.eventType == PluginEventType.ContentChanged) {
            features = this.state.features[Keys.CONTENTCHANGED];
        }

        for (let i = 0; features && i < features?.length; i++) {
            const feature = features[i];
            if (
                (feature.allowFunctionKeys || !hasFunctionKey) &&
                this.editor &&
                feature.shouldHandleEvent(event, this.editor, ctrlOrMeta)
            ) {
                feature.handleEvent(event, this.editor);
                if (isKeyDownEvent) {
                    event.handledByEditFeature = true;
                }
                break;
            }
        }
    }
}

/**
 * @internal
 * Create a new instance of EditPlugin.
 */
export function createEditPlugin(): PluginWithState<EditPluginState> {
    return new EditPlugin();
}
